import React, { useEffect, useRef, useState } from "react";
import { Dialog, Input, Loader } from "../utils";
import { SearchDropdown, Dropdown } from "../utils";
import { Button } from "../utils";
import { buttonClasses } from "../utils/theme";
import { useCheckpointData, useCheckpoints, usePlant, useTripDetails } from "../../services/hooks";
import { useUpdateManageTripMutation, useModifyTripMutation } from "../../api/api";
import { useCustomizeTripWorkflowMutation, useGetApprovalConfigMutation, useGetApprovalDetailMutation, useVehicleDetailsMutation } from "../../api/vmsSlice";
import { capitalizeCamelCase, capitalizeSentence, findDeepValue, formatDate, isFilePath, parseUnderscore, removeSpecialChars } from "../../services/functions";
import ApprovalHistory from "./ApprovalHistory";
import { toastMessage } from "../utils";
import "./Trip.css";
import { useLocation, useNavigate } from "react-router";
import {  CameraEntryIcon, CloseIcon, SearchIcon } from "../../services/icons";
import DynamicFormRender from "../Dynamic/DynamicForm";
import { useUserDetail } from "../../services/hooks";
import { useUserPermissions } from "../../services/hooks";
import { useGetTripcheckpointMutation } from "../../api/vmsSlice";
import { CheckIcon } from "../../services/icons";
import AllCheckpointData from "../Dashboard/AllCheckpointData";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FilePreview from "../utils/FilePreview";
import { useSearchParams } from "react-router-dom";
import GenericWorkflowUpdate from "../utils/GenericWorkflowUpdate";
import { usePlantContext } from "../../context/plantContext";
// import {  } from "../utils/index";
import { Box, Typography, Chip } from "@mui/material";
import Fixed_reader from "../../Images/fixed_reader.svg";
import Handheld_reader from '../../Images/handheld_Reader.svg'

const defaultFilter = {
	checkpointId: "all",
};

const passOn = /passon/i;
const moveTo = /moveto/i;

// ========== HELPER FUNCTIONS FOR DURATION CALCULATIONS ==========

const calculateDuration = (startTime, endTime) => {
	if (!startTime || !endTime) return "-";
	
	const start = new Date(startTime);
	const end = new Date(endTime);
	
	if (isNaN(start.getTime()) || isNaN(end.getTime())) return "-";
	
	const diffMs = end - start;
	if (diffMs < 0) return "-";
	
	const diffSeconds = Math.floor(diffMs / 1000);
	const diffMinutes = Math.floor(diffSeconds / 60);
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);
	
	if (diffDays > 0) {
		const remainingHours = diffHours % 24;
		const remainingMinutes = diffMinutes % 60;
		return `${diffDays}d ${remainingHours}h ${remainingMinutes}m`;
	} else if (diffHours > 0) {
		const remainingMinutes = diffMinutes % 60;
		return `${diffHours}h ${remainingMinutes}m`;
	} else if (diffMinutes > 0) {
		const remainingSeconds = diffSeconds % 60;
		return `${diffMinutes}m ${remainingSeconds}s`;
	} else {
		return `${diffSeconds}s`;
	}
};

const calculateTotalTripDuration = (vehicleData) => {
	if (!vehicleData?.statusHistory?.length) return "-";
	
	const sortedHistory = [...vehicleData.statusHistory].sort(
		(a, b) => new Date(a.inTime) - new Date(b.inTime)
	);
	
	const firstEntry = sortedHistory[0]?.inTime;
	
	let lastTime = null;
	for (let i = sortedHistory.length - 1; i >= 0; i--) {
		if (sortedHistory[i].outTime) {
			lastTime = sortedHistory[i].outTime;
			break;
		} else if (sortedHistory[i].inTime) {
			lastTime = sortedHistory[i].inTime;
			break;
		}
	}
	
	if (!firstEntry) return "-";
	
	if (!vehicleData.trip_over && !lastTime) {
		lastTime = new Date().toISOString();
	}
	
	return calculateDuration(firstEntry, lastTime || new Date().toISOString());
};

const calculateCheckpointDuration = (status) => {
	if (!status?.inTime) return null;
	const outTime = status.outTime || new Date().toISOString();
	return calculateDuration(status.inTime, outTime);
};

const matchStatusHistoryToWorkflow = (checkpointList, statusHistory) => {
	if (!statusHistory?.length || !checkpointList?.length) return new Map();
	
	// Sort statusHistory by inTime to ensure chronological order
	const sortedHistory = [...statusHistory].sort(
		(a, b) => new Date(a.inTime) - new Date(b.inTime)
	);
	
	// Track which statusHistory entries have been assigned
	const assignedStatusIds = new Set();
	
	// Map: order -> { status, allStatus }
	const orderToStatusMap = new Map();
	
	// Process checkpoints in order
	const sortedCheckpoints = [...checkpointList].sort((a, b) => a.order - b.order);
	
	for (const checkpoint of sortedCheckpoints) {
		if (!checkpoint.visited) continue;
		
		const checkpointId = checkpoint.checkpoints?.[0]?._id;
		if (!checkpointId) continue;
		
		// Find the next unassigned status entry for this checkpoint
		const matchingStatus = sortedHistory.find(
			(s) => s.checkpointId === checkpointId && !assignedStatusIds.has(s._id)
		);
		
		if (matchingStatus) {
			assignedStatusIds.add(matchingStatus._id);
			
			// Get all status entries with the same _id (for multiple form entries at this specific visit)
			// Note: allStatus should only contain entries related to THIS specific visit, not all visits to the checkpoint
			const allStatusForThisVisit = sortedHistory.filter(
				(s) => s._id === matchingStatus._id || 
					   (s.checkpointId === checkpointId && s.checkInDataID === matchingStatus._id)
			);
			
			orderToStatusMap.set(checkpoint.order, {
				status: matchingStatus,
				allStatus: allStatusForThisVisit
			});
		}
	}
	
	return orderToStatusMap;
};

// Updated calculateTransitDuration that works with the order-based mapping...
const calculateTransitDurationBetweenOrders = (currentOrder, nextOrder, orderToStatusMap) => {
	const currentStatusData = orderToStatusMap.get(currentOrder);
	const nextStatusData = orderToStatusMap.get(nextOrder);
	
	if (!currentStatusData?.status?.outTime || !nextStatusData?.status?.inTime) return null;
	
	return calculateDuration(currentStatusData.status.outTime, nextStatusData.status.inTime);
};
export default function TripIndex() {
	const { plantId } = usePlantContext();
	const filterRef = useRef(defaultFilter);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const entryId = searchParams.get("entryId");
	const { userPermissions } = useUserPermissions();
	const [isModifying, setIsModifying] = useState(false);
	const [searchVehicle, setSearchVehicle] = useState("");
	const [searchTrigger, setSearchTrigger] = useState(false);
	const [selectedVehicle, setSelectedVehicle] = useState({});
	const [vehicledata, setVehicleData] = useState({});
	const [vehicleTableHeader, setVehicleTableHeader] = useState([]);
	const [allCheckpointList, setAllCheckpointList] = useState([]);
	const [forcedEndTrip, setForcedEndTrip] = useState(false);
	const [cancelTrip, setCancelTrip] = useState(false);
	const [showDefaultListing, setShowDefaultListing] = useState(true);
	// const [remarks, setRemarks] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState({
		searchBy: "",
		checkpointId: "",
		remarks: "",
	});
	const [logStatus, setLogStatus] = useState({});
	const [tripData, setTripData] = useState([]);
	const [isOpen, setIsOpen] = useState(false);
	const [viewShowModel, setViewShowModel] = useState('tripDetail')
	const { userDetail } = useUserDetail();
	const [updateCustomizeTripWorkflow] = useCustomizeTripWorkflowMutation();
	// const { trigger, checkpointData, isLoading, isFetching, count, message, error: checkpointError } = useCheckpointData(filterRef.current);
	const { getTripDetail, tripDetail, isLoading, message, error: tripDataError } = useTripDetails();
	const [updateManageTrip] = useUpdateManageTripMutation();
	const [modifyTripTrigger] = useModifyTripMutation();
	const [vehicleDetailTrigger] = useVehicleDetailsMutation();
	const [getTripcheckpointTrigger] = useGetTripcheckpointMutation();
	const location = useLocation();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [historicalDataHeaderMap, setHistoricalDataHeaderMap] = useState({});
	const { checkoutConfigMap } = useCheckpoints();
	const { plants } = usePlant();
	const factoryCoordinatesValue = plants.find((_) => _._id === plantId)?.factoryCoordinates;
	const handleChange = (value, key) => {
		// console.log('value', value, 'key', key);
		setSearchVehicle((previous) => {
			return { [key]: value === null ? "" : value };
		});
	};

	const onhandleClear = () => {
		setSearchTrigger(true);
		setSearchVehicle("");
	};

	const handleOpenNewTab = (data) => {
		// Construct the new URL based on the current location
		const newRoute = "/map-view"; // Replace with your desired route
		const newUrl = `${window.location.origin}${newRoute}?data=${encodeURIComponent(data)}`;

		// Open the new route in a new tab
		window.open(newUrl, "_blank");
	};

	const onHandleSearch = () => {
		// console.log('search', searchVehicle);
		setIsModifying(false);
		// convertTextToSpeech(JSON.stringify(searchVehicle));
		if (!searchVehicle) {
			setError({ ...error, searchBy: "Please entered search value" });
		} else {
			setError({ ...error, searchBy: "" });
		}
		const trimmedSearch = {};
		Object.keys(searchVehicle).forEach((key) => {
			if (typeof searchVehicle[key] === "string") {
				trimmedSearch[key] = searchVehicle[key].trim();
			} else {
				trimmedSearch[key] = searchVehicle[key];
			}
		});
		setSearchVehicle(trimmedSearch);
		filterRef.current = {
			...filterRef.current,
			...{ searchBy: Object.keys(searchVehicle), search: Object.values(trimmedSearch), start: new Date(Date.now() - 250 * 24 * 60 * 60 * 1000).toISOString, end: new Date().toISOString() },
		};
		if (Object.keys(searchVehicle).length > 0) {
			filterRef.current = { ...filterRef.current, plantId };
			setSearchTrigger(false);
			// trigger(filterRef.current);
			getTripDetail(filterRef.current);
		}
	};

	// console.log('checkpointData', checkpointData);
	useEffect(() => {
		if (entryId) {
			setLoading(true);
			getTripcheckpointTrigger({ type: "all", tripId: entryId })
				.unwrap()
				.then((res) => {
					// console.log('detail-chk', res.data);
					if (res?.error) {
						toastMessage(false, res.message);
					} else {
						setAllCheckpointList(res?.data || []);
					}
				})
				.catch((err) => {
					// console.log("err", err);
				})
				.finally(() => {
					setLoading(false);
				});
			vehicleDetailTrigger({ vehicleEntryId: entryId })
				.unwrap()
				.then((res) => {
					// console.log('detail-status-data', res.data.data);
					// console.log('detail-status', res.data.statusHistory);
					setVehicleData(res?.data || {});
					setSelectedVehicle(res?.data || {});
					setVehicleTableHeader(res?.tableHeader || []);
					setHistoricalDataHeaderMap(res?.historicalDataHeaderMap);
					// generateDetail(res.data, res.tableHeader)
				})
				.catch((err) => {
					// console.log("err", err);
				});
		}
	}, [entryId]);
	useEffect(() => {
		setSelectedVehicle({});
		setVehicleData({});
		setAllCheckpointList([]);
		setShowDefaultListing(true);
	}, [plantId]);
	useEffect(() => {
		if (location?.state?.data?._id) {
			handleVehicleDetail(location.state.data._id);
		}
	}, [location]);

	useEffect(() => {
		if (location.state?.selectedVehicle) {
			setSelectedVehicle(location.state.selectedVehicle);
			handleVehicleDetail(location.state.selectedVehicle._id);
		}
	}, [location.state]);
	const handleVehicleDetail = (id) => {
		setShowDefaultListing(false);
		setLoading(true);
		getTripcheckpointTrigger({ type: "all", tripId: id })
			.unwrap()
			.then((res) => {
				// console.log('detail-chk', res.data);
				if (res?.error) {
					toastMessage(false, res.message);
				} else {
					setAllCheckpointList(res?.data || []);
					setTripData(res?.data || []);
				}
			})
			.catch((err) => {
				setShowDefaultListing(true);
				// console.log("err", err);
			})
			.finally(() => {
				setLoading(false);
			});
		vehicleDetailTrigger({ vehicleEntryId: id })
			.unwrap()
			.then((res) => {
				// console.log('detail-status-data', res.data.data);
				// console.log('detail-status', res.data.statusHistory);
				setVehicleData(res?.data || {});
				setSelectedVehicle(res?.data || {});
				setVehicleTableHeader(res?.tableHeader || []);
				setHistoricalDataHeaderMap(res?.historicalDataHeaderMap);
				// generateDetail(res.data, res.tableHeader)
			})
			.catch((err) => {
				setShowDefaultListing(true);
				// console.log("err", err);
			});
	};

	const handleLogStatus = (status) => {
		let TempStatus = JSON.parse(JSON.stringify({ ...logStatus }));
		if (TempStatus.hasOwnProperty(status) && TempStatus[status] === true) {
			TempStatus[status] = false;
		} else {
			TempStatus[status] = true;
		}
		setLogStatus(TempStatus);
	};

	function flattenObj(res, obj = {}) {
		for (let key in obj) {
			if (Array.isArray(obj[key])) {
				if (obj[key].every((i) => isFilePath(i))) {
					res[key] = obj[key];
				} else if (obj[key].every((i) => typeof i == "string")) {
					res[key] = obj[key].join(",");
				} else if (obj[key].every((i) => typeof i === "object")) {
					if (obj[key].every((subObj) => typeof subObj[key + "Name"] === "string")) {
						// static hack for camelcase [key]Name array of objects
						res[key] = obj[key].reduce((p, c) => p + c[key + "Name"] + ", ", "").replace(/,\s*$/, "");
					}
				}
			} else if (typeof obj[key] == "object") {
				flattenObj(res, obj[key]);
			} else {
				res[key] = obj[key];
			}
		}
	}
const generateDetail = (data, checkpointList) => {
	if (!data) return [];
	
	// Pre-compute the status mapping based on workflow order
	const orderToStatusMap = matchStatusHistoryToWorkflow(checkpointList, data.statusHistory);
	
	// Sort checkpoints by order for proper transit duration calculation
	const sortedCheckpointList = [...checkpointList].sort((a, b) => a.order - b.order);
	
	const newStaticDetail =
		sortedCheckpointList?.map((ele, index) => {
			const { order, checkpoints = [], optional, visited, approvalStatus } = ele;
			let currChkpt = checkpoints[0];
			
			// Get allowMultipleEntry from inside checkpoints[0]
			const allowMultipleEntry = currChkpt?.allowMultipleEntry || false;
			
			let checkPointStatus = checkpoints.map((chk, idx) => (
				<div 
					style={visited ? (approvalStatus === "RejectToGateout" ? { backgroundColor: "red" } : { backgroundColor: "var(--primary-color)" }) : { backgroundColor: "var(--dark-gray-color)" }} 
					className="status-clip" 
					key={`checkpoint-key-${idx}`}
				>
					{chk.displayName}
				</div>
			));
			
			// Get the status for THIS specific order (not just the first one found)
			const statusData = orderToStatusMap.get(order);
			const status = visited ? statusData?.status : null;
			
			// For allowMultipleEntry checkpoints, get ALL status entries for this checkpoint
			// For regular checkpoints, use only the matched status
			let allStatus = [];
			if (allowMultipleEntry && visited && currChkpt?._id) {
				// Get ALL statusHistory entries for this checkpoint
				allStatus = data.statusHistory.filter(s => s.checkpointId === currChkpt._id);
			} else {
				allStatus = visited ? statusData?.allStatus || [] : [];
			}
			
			let type;
			let isOfflinePacket = status?._basis == "offline-packet";
			let rows = [];

			// Calculate time spent at this checkpoint
			const checkpointTimeSpent = status ? calculateCheckpointDuration(status) : null;

			// Calculate transit duration to next checkpoint using order-based mapping
			let transitDuration = null;
			if (index < sortedCheckpointList.length - 1 && status?.outTime) {
				const nextCheckpoint = sortedCheckpointList[index + 1];
				if (nextCheckpoint?.visited) {
					transitDuration = calculateTransitDurationBetweenOrders(
						order, 
						nextCheckpoint.order, 
						orderToStatusMap
					);
				}
			}

			const { hideInTime = false, hideOutTime = false, inTimeLabel = "In Time", outTimeLabel = "Out Time" } = checkoutConfigMap[currChkpt?._id] || {};

			// For allowMultipleEntry checkpoints, find ALL data entries for this checkpoint
			// For regular checkpoints, use time-window based matching
			let relatedDataEntries = [];
			
			if (visited && currChkpt?._id) {
				if (allowMultipleEntry) {
					// For allowMultipleEntry, get ALL data entries for this checkpointId
					relatedDataEntries = data?.data?.filter((_) => _.checkpointId === currChkpt._id) || [];
				} else if (status) {
					// For regular checkpoints, use the original matching logic
					relatedDataEntries = data?.data?.filter((_) => 
						_._id === status._id || 
						_._id === status.checkInDataID ||
						_._id === status.checkOutDataID
					) || [];
				}
				
				// Sort entries by created_on to maintain chronological order
				relatedDataEntries.sort((a, b) => new Date(a.created_on) - new Date(b.created_on));
			}
			

			const seen = new Set();

			// Process related data entries for this specific checkpoint visit
			for (const dataEntry of relatedDataEntries) {
				if (seen.has(dataEntry._id)) continue;
				seen.add(dataEntry._id);
				
				type = dataEntry.type;
				let currentTableHeader = historicalDataHeaderMap[`${dataEntry.checkpointId}-${dataEntry.type}`];
				let row = [];
				
				// Find the corresponding status entry for this data entry
				const matchingStatus = allStatus.find(s => 
					s._id === dataEntry._id || 
					s.checkInDataID === dataEntry._id ||
					s.checkOutDataID === dataEntry._id
				) || status;
				
				if (currentTableHeader) {
					if (!hideInTime) {
						let inTime = formatDate(matchingStatus?.inTime || status?.inTime);
						row.push(
							<div className="detail_row" key={inTime + currChkpt?._id + inTimeLabel + dataEntry._id}>
								<div className="detail_row_title">{inTimeLabel}</div>
								<div className="detail_row_value">{inTime}</div>
							</div>
						);
					}
					if (!hideOutTime) {
						let outTime = formatDate(matchingStatus?.outTime || status?.outTime);
						row.push(
							<div className="detail_row" key={outTime + currChkpt?._id + outTimeLabel + dataEntry._id}>
								<div className="detail_row_title">{outTimeLabel}</div>
								<div className="detail_row_value">{outTime}</div>
							</div>
						);
					}
					currentTableHeader.forEach((header, i) => {
						let value = findDeepValue(dataEntry, header.name?.split("."));
						if (value == "-") {
							value = findDeepValue(dataEntry.formData, header.name?.split("."));
						}
						if (Array.isArray(value)) {
							value = new Array(...value);
						} else {
							value = String(value);
						}
						if (value && value != "-") {
							const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
							if (new Date(value) != "Invalid Date" && isoPattern.test(value)) {
								value = formatDate(value);
							}
							row.push(
								<div className="detail_row" key={`${i}-${dataEntry._id}`}>
									<div className="detail_row_title">{header.label}</div>
									<div className="detail_row_value">
										{Array.isArray(value) ? (
											<span style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
												{value.map((el, i) => (isFilePath(el) ? <FilePreview key={i} fileKey={el} extension={String(el).split(".").pop()} /> : capitalizeCamelCase(el)))}
											</span>
										) : isFilePath(value) ? (
											<FilePreview fileKey={value} extension={value.split(".").pop()} />
										) : (
											capitalizeCamelCase(value)
										)}
									</div>
								</div>
							);
						}
					});
				} else {
					let rowData = {};
					flattenObj(rowData, dataEntry.formData);
					
					if (!hideInTime) {
						rowData[inTimeLabel] = formatDate(matchingStatus?.inTime || status?.inTime);
					}
					if (!hideOutTime) {
						rowData[outTimeLabel] = formatDate(matchingStatus?.outTime || status?.outTime);
					}
					// if (dataEntry.type) {
					// 	rowData["Type"] = dataEntry.type;
					// }

					if (approvalStatus === "RejectToGateout") {
						delete rowData[inTimeLabel];
						delete rowData[outTimeLabel];
					}
					delete rowData._id;
					delete rowData.vehicleId;
					
					for (let key in rowData) {
						let value = rowData[key];
						if (Array.isArray(value)) {
							value = new Array(...value);
						} else {
							value = String(value);
						}
						const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
						if (new Date(value) != "Invalid Date" && isoPattern.test(value)) {
							value = formatDate(value);
						}
						row.push(
							<div className="detail_row" key={`${key}-${rowData[key]}-${dataEntry._id}`}>
								<div className="detail_row_title">{capitalizeSentence(parseUnderscore(capitalizeCamelCase(key)))}</div>
								<div className="detail_row_value">
									{Array.isArray(value) ? (
										<span style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
											{value.map((el, i) => (isFilePath(el) ? <FilePreview key={i} fileKey={el} extension={String(el).split(".").pop()} /> : capitalizeCamelCase(el)))}
										</span>
									) : isFilePath(value) ? (
										<FilePreview fileKey={value} extension={value.split(".").pop()} />
									) : (
										capitalizeCamelCase(value)
									)}
								</div>
							</div>
						);
					}
				}
				
				if (row.length > 0) {
					rows.push(row);
				}
			}
			
			
			// Create a unique key for log status based on order to handle same checkpoint at different orders
			const logStatusKey = `${status?.displayName || currChkpt?.displayName}-order-${order}`;
			
			const view = (
				<div key={`${index}-order-${order}`} className="statusHistory-container">
					<div className="statuslayout-container">
						<div className="status-connector-container">
							<div
								style={index === 0 ? { backgroundColor: "var(--background-color)" } : visited ? { backgroundColor: "green" } : { backgroundColor: "var(--dark-gray-color)" }}
								className="status-connector-line"
							></div>
							<div style={visited ? (approvalStatus === "RejectToGateout" ? { backgroundColor: "red" } : { backgroundColor: "green" }) : { backgroundColor: "var(--dark-gray-color)" }} className="status-connector-circle">
								{visited ? (approvalStatus === "RejectToGateout" ? <CloseIcon fontSize={"small"} htmlColor={"#fff"} /> : <CheckIcon fontSize={"small"} htmlColor={"#fff"} />) : null}
							</div>
							{index < sortedCheckpointList.length - 1 && (
								<div
									style={visited ? { backgroundColor: "green" } : { backgroundColor: "var(--dark-gray-color)" }}
									className={`status-connector-line bottom-line ${transitDuration ? 'with-transit-duration' : ''}`}
								></div>
							)}

							{transitDuration && (
								<div className="transit-duration-chip">
									<Chip
										label={transitDuration}
										size="small"
										sx={{
											backgroundColor: "#2e7d32",
											color: "white",
											fontSize: "10px",
											height: "20px",
											fontWeight: 600,
											"& .MuiChip-label": { px: 1 },
											minWidth: "80px",
										}}
									/>
								</div>
							)}
						</div>
						<div className="status-checkpoint-container">{checkPointStatus}</div>
					</div>
					<Box
						className="statusHistory-detail-content"
						sx={{ position: "relative" }}
						style={{
							backgroundColor: "#fff",
							borderRadius: "8px",
							boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
							border: "1px solid var(--primary-color)",
							borderLeft: "4px solid var(--primary-color)",
						}}
					>
						<Box
							sx={{
								position: "absolute",
								top: 8,
								right: 12,
								display: "flex",
								alignItems: "center",
								gap: "6px",
							}}
						>
							{status?.type === "camera" && (
								<CameraEntryIcon sx={{ fontSize: 18, color: "#ff7700" }} />
							)}
							{status?.type === "scan" && (
								<img src={Fixed_reader} alt="reader" height="25rem" width="25rem" />
							)}
							{/* {status?.type === "manual" && (
								<img src={Handheld_reader} alt="reader" height="25rem" width="25rem" />
							)} */}

							{checkpointTimeSpent && checkpointTimeSpent !== "0s" && (
								<Chip
									label={checkpointTimeSpent}
									size="small"
									sx={{
										backgroundColor: "#2e7d32",
										color: "white",
										fontSize: "11px",
										height: "20px",
										fontWeight: 600,
										marginTop: "5px",
										"& .MuiChip-label": { px: 1 },
									}}
								/>
							)}
						</Box>

						<div style={{ fontWeight: "bold", textAlign: "center", color: "var(--text-color)", position: "relative" }}>
							{status?.displayName || currChkpt?.displayName || ""}
							{type && ` (${capitalizeCamelCase(type)})`}
							{isOfflinePacket ? <span className="offline-packet">Offline</span> : null}
						</div>
						
						{/* Only show "Show Full Status Log" button if allowMultipleEntry is true AND there are multiple rows */}
						{allowMultipleEntry && rows.length > 1 && (
							<div className="logbutton-div">
								<button onClick={() => handleLogStatus(logStatusKey)} className="logbutton">
									{logStatus[logStatusKey] ? "- " : "+ "}Show Full Status Log
								</button>
							</div>
						)}
						
						<div style={{ padding: "0.5rem", borderRadius: "12px", color: "var(--text-color)" }}>
							{/* If allowMultipleEntry is true and log is expanded, show all rows; otherwise show only the last row */}
							{allowMultipleEntry && logStatus[logStatusKey]
								? rows.map((row, i) =>
									row?.length ? (
										<div key={i} style={{ border: "1px dotted var(--gray-color)", marginBottom: "10px", padding: "5px", borderRadius: "10px" }}>
											{row}
										</div>
									) : null
								)
								: [rows[rows?.length - 1]].map((row, i) =>
									row?.length ? (
										<div key={i}>{row}</div>
									) : (
										<div key={i} className="detail_row">
											<div className="detail_row_title">In Time</div>
											<div className="detail_row_value">-</div>
										</div>
									)
								)}

							{currChkpt?.sendApproval ? (
								<ApprovalStatus vehicleEntryDetail={data} checkpointDetail={currChkpt} historicalDataHeaderMap={historicalDataHeaderMap} />
							) : null}
						</div>
					</Box>
				</div>
			);
			return view;
		}) || [];
	return <>{newStaticDetail}</>;
};

	useEffect(() => {
		if (tripDataError && !isLoading) {
			toastMessage(false, message);
		}
	}, [isLoading]);
	//dynamic form
	const handleClose = () => {
		// console.log('handleClose called');
		setIsModifying(false);
		setForcedEndTrip(false);
		setCancelTrip(false);
		setIsSubmitting(false);
	};
	const handleFormSubmit = (data) => {
		let payload;
		setIsSubmitting(true);
		if (isModifying) {
			payload = {
				vehicleEntryId: selectedVehicle._id,
				...data?.data,
			};
			// console.log("Form submitted", payload);
			modifyTripTrigger(payload)
				.unwrap()
				.then((res) => {
					if (res?.error) {
						return toastMessage(false, res.message);
					}
					// toastMessage(true, 'Trip Updated Successfully');
					toastMessage(true, res.message);
					setIsModifying(false);
					setForcedEndTrip(false);
					setCancelTrip(false);
					handleVehicleDetail(selectedVehicle._id);
					setSelectedVehicle({});
					setSearchVehicle("");
					setSearchTrigger(false);
					setIsSubmitting(false);
				})
				.catch((err) => {
					toastMessage(false, err.message);
				});
		} else {
			payload = {
				vehicleEntryId: selectedVehicle._id,
				// vehicleEntryId: selectedVehicle._id,
				forceEndTrip: forcedEndTrip,
				cancelTrip: cancelTrip,
				data: data.data,
			};

			updateManageTrip(payload)
				.unwrap()
				.then((res) => {
					setIsSubmitting(false);
					if (res?.error) {
						return toastMessage(false, res.message);
					}
					toastMessage(true, res.message);
					setIsModifying(false);
					setForcedEndTrip(false);
					setCancelTrip(false);
					handleVehicleDetail(selectedVehicle._id);
					// toastMessage(true, 'Trip Updated Successfully')
					setSelectedVehicle({});
					setSearchVehicle("");
					setSearchTrigger(false);
				})
				.catch((err) => {
					toastMessage(false, err.message);
				});
		}
	};
	const handleDialogOpen = () => {
		setIsOpen(true);
	};
	const onClose = () => {
		setIsOpen(false);
	};
	const handleTripWorkflowUpdate = async () => {
		const payload = {
			vehicleEntryId: selectedVehicle._id,
			checkpoints: tripData,
		};
		// console.log("Payload", payload)
		await updateCustomizeTripWorkflow(payload)
			.unwrap()
			.then((res) => {
				if (res.error == true) {
					toastMessage(false, res.message);
				} else {
					toastMessage(true, res.message);
					onClose();
				}
			})
			.catch((err) => {
				toastMessage(false, err.message);
			});
	};
	function generateFormType() {
		if (isModifying) return { title: "Modify Trip", template: "modifyTrip" };
		if (cancelTrip) return { title: "Cancel Trip", template: "cancelTrip" };
		if (forcedEndTrip) return { title: "Force End Trip", template: "forceEndTrip" };
		return { title: "Vehicle Detail", template: "" };
	}

	// Calculate total trip duration
	const totalTripDuration = calculateTotalTripDuration(vehicledata);

	return (
		<>
			<div className="contentpanel" onClick={() => (!searchTrigger ? setSearchTrigger(true) : null)}>
				<div className="cust-row flex-algn-cent">
					<div className="cust-col-5">
						<h1 className="title">Manage Trip</h1>
					</div>
					<div className="cust-col-5 flex-jc-end gap-3"></div>
				</div>
				<hr className="bgg" />
				<div className="trip-search-section">
					<div className="trip-search-section-left">
						<SearchDropdown
							id={"vehicleNumber"}
							name={"vehicleNumber"}
							options={[
								{ label: "Vehicle Number", value: "vehicleNumber" },
								{ label: "Trip ID", value: "trip_counter" },
							]}
							preSelectedOption={{ label: "Vehicle Number", value: "vehicleNumber" }}
							label={"Search by"}
							placeholder={"Enter value"}
							value={searchVehicle}
							Debounce={true}
							textBorderRadius="1px 0px 0px 1px"
							selectBorderRadius="20px 1px 1px 20px"
							adornment={{
								is: true,
								onClick: () => {
									setSearchTrigger(true);
									setSearchVehicle("");
								},
								type: isLoading ? "loader" : "CloseIcon",
							}}
							onDropdownChange={onhandleClear}
							onChange={(value, key) => handleChange(value, key)}
							onhandleClear={onhandleClear}
						/>
						<div style={{ textAlign: "start" }}>{error?.searchBy ? <span style={{ color: "var(--red-color)", fontSize: "12px" }}>{error.searchBy}</span> : null}</div>
						<div>
							{!searchTrigger && tripDetail?.length > 0 ? (
								<div className={"trip-search-section-left-list"}>
									<ul>
										{tripDetail.map((ele, index) => (
											<li
												className={ele._id === selectedVehicle._id ? "active" : null}
												key={ele._id}
												onClick={() => {
													// console.log('selected vehicle', ele)
													handleVehicleDetail(ele._id);
												}}
											>
												<span style={{ fontSize: "14px", fontWeight: "bold" }}>Vehicle No.</span>
												<span style={{ fontSize: "14px" }}>{ele.vehicleNumber}</span>
												<span style={{ fontSize: "14px", fontWeight: "bold" }}>, Trip ID : </span>
												<span style={{ fontSize: "14px" }}>{`${ele.trip_counter}`}</span>
											</li>
										))}
									</ul>
								</div>
							) : null}
						</div>
					</div>
					<div className="trip-search-section-right">
						<Button
							text={<SearchIcon />}
							style={{ ...buttonClasses.lynkitOrangeFill, height: "45px", minWidth: "45px", borderRadius: "50%", transform: "Translate(-12px, -3px)" }}
							onClick={() => {
								onHandleSearch();
							}}
						/>
					</div>
				</div>
				{loading ? (
					<Loader size="2rem" />
				) : !showDefaultListing && Object.keys(vehicledata).length > 0 ? (
					<div className="trip-detail-section">
						<div className="trip-detail-container">
							{/* Header with Vehicle Number */}
							<Box
								className="trip-detail-header"
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Typography sx={{ color: "white", fontWeight: 600, fontSize: "1.2rem" }}>
									{generateFormType()?.title}
								</Typography>

								{vehicledata?.vehicleNumber && (
								<Typography sx={{ color: "white", fontWeight: 600, fontSize: "1.2rem" }}>
									- {vehicledata.vehicleNumber}
								</Typography>
								)}

								{vehicledata?.trip_status && (
								<Chip
									label={vehicledata.trip_status}
									size="small"
									sx={{
									ml: 1,
									height: 22,
									fontWeight: 600,
									color: "white",
									backgroundColor:
										vehicledata.trip_status === "Ongoing"
										? "#1976d2"
										: vehicledata.trip_status === "Completed"
										? "#2e7d32"
										: vehicledata.trip_status === "Cancelled"
										? "#d32f2f"
										: "#757575",
									}}
								/>
								)}
								{vehicledata?.approvalStatus && (
								<Chip
									label={vehicledata.approvalStatus}
									size="small"
									sx={{
									ml: 1,
									height: 22,
									fontWeight: 600,
									color: "white",
									backgroundColor:
										(passOn.test(vehicledata.approvalStatus) || moveTo.test(vehicledata.approvalStatus))
										? "#1976d2"
										: vehicledata.approvalStatus === "Approved"
										? "#2e7d32"
										: vehicledata.approvalStatus === "Rejected"
										? "#d32f2f"
										: vehicledata.approvalStatus === "Hold"
										? "#e6cf07ff"
										: vehicledata.approvalStatus === "Pending" 
										? "#1976d2"
										: "#757575",
									}}
								/>
								)}

							</Box>

							<button
								style={{ background: "transparent", border: "none", color: "white", cursor: "pointer" }}
								onClick={() => {
									setIsModifying(false);
									setForcedEndTrip(false);
									setCancelTrip(false);
									setIsSubmitting(false);
									setShowDefaultListing(true)
								}}
							>
								<CloseIcon />
							</button>
							</Box>

							<div className="trip-button-container">
								{userPermissions?.modifyTrip?.value && !(isModifying || forcedEndTrip || cancelTrip) && !selectedVehicle?.trip_over && (
									<Button
										onClick={() => setIsModifying(!isModifying)}
										text="Modify Trip"
										style={{ ...buttonClasses.lynkitOrangeFill, height: "36px", marginRight: "10px", width: "150px" }}
									/>
								)}
								{userPermissions?.cancelTrip?.value && !(isModifying || forcedEndTrip || cancelTrip) && !selectedVehicle?.trip_over && (
									<Button
										text="Cancel Trip"
										style={{ ...buttonClasses.lynkitOrangeFill, height: "36px", marginRight: "10px", width: "150px" }}
										onClick={() => {
											setCancelTrip(true);
										}}
									/>
								)}
								{userPermissions?.forceEndTrip?.value && !(isModifying || forcedEndTrip || cancelTrip) && !selectedVehicle?.trip_over && (
									<Button
										text="Force End Trip"
										style={{ ...buttonClasses.lynkitOrangeFill, height: "36px", marginRight: "10px", width: "150px" }}
										onClick={() => {
											setForcedEndTrip(true);
										}}
									/>
								)}
								{userPermissions?.viewTripMap?.value && factoryCoordinatesValue && (
									<Button
										// onClick={() => handleOpenNewTab(selectedVehicle._id)}
										// onClick={()=>navigate(`/map-view?data=${encodeURIComponent(selectedVehicle._id)}`)}
										onClick={() => navigate(`/map-view`, { state: { selectedVehicle, factoryCoordinatesValue: factoryCoordinatesValue } })}
										// text="View On Map"
										style={{ ...buttonClasses.lynkitOrangeFill, height: "36px", marginRight: "10px", width: "150px" }}
									>
										<LocationOnIcon />
										View On Map
									</Button>
								)}
								{userPermissions?.customizeTripWorkflow?.value && tripData.length > 0 && !tripData[tripData.length - 1]?.visited && (
									<Button style={{ ...buttonClasses.lynkitOrangeFill, height: "36px", marginRight: "10px", width: "190px" }} onClick={handleDialogOpen}>
										Customize Trip WorkFlow
									</Button>
								)}
							</div>
							{isOpen && (
								<>
									<Dialog
										size="lg"
										open={true}
										handleSubmit={handleTripWorkflowUpdate}
										handleClose={() => {
											// setLabelDetail(labelData);
											onClose();
										}}
										handleCancel={onClose}
										title={"Customize Trip Workflow"}
										SubmitText={"Update"}
									>
										<GenericWorkflowUpdate tripData={tripData} workflowData={tripData} setWorkflowData={setTripData} />
									</Dialog>
								</>
							)}
							{isModifying || forcedEndTrip || cancelTrip ? (
								<>
									<div style={{ padding: "0px 20px" }}>
										{isModifying && (
											<div>
												<p
													style={{ color: "var(--red-color)", fontSize: "14px" }}
												>{`**Note: Once you click Submit the trip the current trip with ID ${selectedVehicle?.trip_counter} will be concluded, and the timestamp for this action will be recorded as the current time for upcoming checkpoints of current trip.`}</p>
											</div>
										)}
										<DynamicFormRender
											templatePayload={{
												organization_id: userDetail.orgId,
												template_name: generateFormType()?.template,
												type: generateFormType()?.template,
											}}
											checkoutData={{ trip_counter: vehicledata.trip_counter, vehicleNumber: vehicledata.vehicleNumber }}
											onClose={handleClose}
											onSubmit={handleFormSubmit}
											isSubmitDisabled={isSubmitting}
										/>
									</div>
								</>
							) : (
								<>
									<div className="trip-detail-content">
										{vehicleTableHeader.length > 0 &&
											vehicleTableHeader
												.filter(ele => ele.label !== "Vehicle No." && ele.label !== "Trip Status")
												.map((ele, index) => (
												<div 
													key={index} 
													className="trip-detail-content-field" 
													style={{
														backgroundColor: "#fff",
														borderRadius: "8px",
														boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
														border: "1px solid var(--primary-color)",
														borderLeft: "4px solid var(--primary-color)",
													}}
												>
													<div
														style={isModifying && (ele.label == "Product" || ele.label == "Product Category") ? { paddingTop: "5px" } : {}}
														className="trip-detail-content-field-label"
													>
														<span>{ele.label} :</span>
													</div>
													<div className="trip-detail-content-field-value">
														{isModifying && (ele.label == "Product" || ele.label == "Product Category") ? (
															<span>
																<Dropdown
																	id={ele.label}
																	name={ele.label == "Product" ? "product" : ele.label == "Product Category" ? "productCategory" : null}
																	value={null}
																	options={[]}
																	label={ele.label}
																	// onChange={(option, key) => onHandleCheckpointDetailChange(option, key)}
																	// error={error?.checkPoint?.orgId}
																/>
															</span>
														) : (
															<span>
																{ele.type == "date" ? formatDate(findDeepValue(vehicledata, ele.name.split("."))) : findDeepValue(vehicledata, ele.name.split("."))}
															</span>
														)}
													</div>
												</div>
											))}
										
										{/* Total Trip Duration - same style as other fields */}
										<div 
											className="trip-detail-content-field" 
											style={{							            
												backgroundColor: "#fff",
												borderRadius: "8px",
												boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
												border: "1px solid var(--primary-color)",
												borderLeft: "4px solid var(--primary-color)",
											}}
										>
											<div className="trip-detail-content-field-label">
												<span>Total Duration :</span>
											</div>
											<div className="trip-detail-content-field-value">
												<span>{totalTripDuration}</span>
											</div>
										</div>
									</div>

									<div>
										<div style={{padding : "15px"}}>
											<Button style={buttonClasses.lynkitOrangeEmpty} text={viewShowModel === 'tripDetail' ? "View Approval History" : "View Trip Detail"} onClick={()=> viewShowModel === 'tripDetail' ?  setViewShowModel('approvalHistory')   : setViewShowModel('tripDetail')} />
										</div>
										{viewShowModel === 'tripDetail' && allCheckpointList.length > 0 && vehicleTableHeader.length > 0 && generateDetail(vehicledata, allCheckpointList)}
										{viewShowModel === 'approvalHistory' && <ApprovalHistory tripDetail={vehicledata} checkpointList={allCheckpointList} />}
									</div>
								</>
							)}
						</div>
					</div>
				) : null}
				{
					showDefaultListing && (
					<>
						<AllCheckpointData listOnly noSocketEvent={true} handletripDetail={(vehicleId) => handleVehicleDetail(vehicleId)} />
					</>
				)
				}
			</div>
		</>
	);
}

function ApprovalStatus({ vehicleEntryDetail, checkpointDetail, historicalDataHeaderMap }) {
	const { userDetail } = useUserDetail();

	const [getApprovalDetail, { data: approvalDetailData, isLoading: isApprovalDetailLoading }] = useGetApprovalDetailMutation();
	const { data: approvalDetails = [] } = approvalDetailData || { data: [] };

	const [getApprovalConfig, { data: approvalConfigData, isLoading: isApprovalConfigLoading }] = useGetApprovalConfigMutation();
	const { data: approvalConfig } = approvalConfigData || { data: [] };

	useEffect(() => {
		if (checkpointDetail) {
			getApprovalDetail({ reference_id: vehicleEntryDetail._id, module: checkpointDetail.checkpointStatus });

			const currentCheckpointEntry = vehicleEntryDetail.statusHistory.find((_) => _.checkpointId == checkpointDetail._id);
			// console.log("currentCheckpointEntry", currentCheckpointEntry)
			if (currentCheckpointEntry?.currentApprovalConfig) {
				getApprovalConfig({ id: currentCheckpointEntry.currentApprovalConfig });
			}
			// let { sendApproval, dependentApproval, approvaldependancy = [], approvalId } = checkpointDetail;
			// if (sendApproval && !dependentApproval) {
			// 	// the approval config selected will always be the approvalId outside
			// 	if (approvalId) {
			// 		// get approval config of this id
			// 		getApprovalConfig({ id: approvalId });
			// 	} else {
			// 		// toastMessage(false, "There is no Approval Config defined for this checkpoint");
			// 	}
			// } else if (sendApproval && dependentApproval) {
			// 	// now we will check the approvalDependancy
			// 	// for each approvalInfo in the approvalDependancy, we will check the itemType and id array and compare it with the data that matches the itemType in the vehicle entry details
			// 	// if the particular id exists in the data, then we will take the corresponding approvalId
			// 	approvaldependancy.forEach((item) => {
			// 		let dataItem = vehicleEntryDetail[item.itemType];
			// 		if (dataItem) {
			// 			// console.log(dataItem, checkpointDetail)
			// 			if (Array.isArray(dataItem)) {
			// 				if (item.itemType == "product" && !item.approvalId) {
			// 					// special case here is the product
			// 					// for Haier, we have product priority usecase
			// 					// so simply, there doesn't exist any approvalConfig, so there won't be any approvalId. In that case, we will simply show whatver approval details exists
			// 				} else if (dataItem.find((_) => item.id.includes(_._id))) {
			// 					// item.approvalId
			// 					if (item.approvalId) {
			// 						// console.log("data item array")
			// 						getApprovalConfig({ id: item.approvalId });
			// 					} else {
			// 						// toastMessage(false, "There is no Approval Config defined for this checkpoint");
			// 					}
			// 				}
			// 			} else {
			// 				if (item.id.includes(dataItem._id)) {
			// 					// item.approvalId
			// 					if (item.approvalId) {
			// 						// console.log("data item", item);
			// 						getApprovalConfig({ id: item.approvalId });
			// 					} else {
			// 						// toastMessage(false, "There is no Approval Config defined for this checkpoint");
			// 					}
			// 				}
			// 			}
			// 		}
			// 	});
			// }
		}
	}, [vehicleEntryDetail]);

	let approvalsUi = [];

	let approvalLayers = [];

	if (approvalConfig?.length) {
		let approval_details = JSON.parse(JSON.stringify(approvalDetails || []));

		approvalConfig[0]?.approval_layer?.forEach((approval_layer, i) => {
			let approvalDetail;
			let approvalIndex;
			for (let i = 0; i < approval_details.length; i++) {
				let item = approval_details[i];

				if (item.approval_detail) {
					if (item.approval_detail?.email == approval_layer.user_details?.email || item.approval_detail.userRole == approval_layer.role) {
						approvalIndex = i;
						break;
					}
				} else {
					if (item.nextApprovalDetails?.find((_) => _.email == approval_layer.user_details?.email || _.userRole == approval_layer.role)) {
						approvalIndex = i;
						break;
					}
				}
			}

			approvalDetail = approval_details[approvalIndex];
			approval_details.splice(approvalIndex, 1);

			// approvalDetail = approvalDetails?.find((item) => {
			// 	if (item.approval_detail) {
			// 		if (approval_layer.require_user_approval) {
			// 			return item.approval_detail.email == approval_layer.user_details?.email;
			// 		} else {
			// 			return item.approval_detail.userRole == approval_layer.role;
			// 		}
			// 	} else {
			// 		return !!item.nextApprovalDetails?.find((_) => _.email == approval_layer.user_details?.email);
			// 	}
			// });
			let approver_detail =
				approvalDetail?.approval_detail || approvalDetail?.nextApprovalDetails?.find((_) => _.email == approval_layer.user_details?.email || _.userRole == approval_layer.role);

			let canApprove = false;
			if (!approvalDetail || approvalDetail.isRejected) {
				if (approver_detail?.id == userDetail?.userId || (!approval_layer.require_user_approval && approval_layer.role_id == userDetail?.userRoleId)) {
					canApprove = true;
				}
				if (canApprove) {
					canApprove = !approvalConfig[0].approval_layer?.some((el, j) => j < i && el.approval_require && (!approvalDetail || approvalDetail.isRejected));
				}
			}
			approvalLayers.push({
				user: approval_layer.require_user_approval ? approval_layer.user_details?.name : approval_layer.role,
				status: getApprovalStatus(approvalDetail),
				canApprove,
				formData: approvalDetail?.formData,
				approvalTableHeader: historicalDataHeaderMap[`${approvalDetail?.approvalTemplateId}-approval`],
				id: approval_layer._id,
			});
		});
	} else if (approvalDetails?.length) {
		approvalDetails?.forEach((approvalDetail, i) => {
			// let canApprove = false;
			// if (!approvalDetail || approvalDetail.isRejected) {
			// 	if (approvalDetail?.approval_detail.id == userDetail?.userId || (!_.require_user_approval && _.role_id == userDetail?.userRoleId)) {
			// 		canApprove = true;
			// 	}
			// 	if (canApprove) {
			// 		canApprove = !approvalConfig[0].approval_layer?.some((el, j) => j < i && el.approval_require && (!approvalDetail || approvalDetail.isRejected));
			// 	}
			// }

			approvalLayers.push({
				user: approvalDetail.approval_detail?.name || approvalDetail.approval_detail?.email || approvalDetail.approval_detail?.userRole,
				status: getApprovalStatus(approvalDetail),
				canApprove: false,
				formData: approvalDetail.formData,
				approvalTableHeader: historicalDataHeaderMap[`${approvalDetail.approvalTemplateId}-approval`],
				id: approvalDetails._id,
			});
		});
	}
	const [approvalFormId, setApprovalFormId] = useState("");
	approvalsUi = approvalLayers.map((_, i) => {
		return (
			<div key={i} className={`approval_status_item ${_.status == "Approved" ? "approved" : _.status == "Rejected" ? "rejected" : "pending"}`}>
				<div className="status-connector-circle">
					{_.status == "Approved" ? <CheckIcon fontSize={"small"} htmlColor={"#fff"} /> : _.status == "Rejected" ? <CloseIcon fontSize="small" htmlColor="#fff" /> : null}
				</div>
				<div style={{ textAlign: "center" }}>
					<div>Status : {_.status}</div>
					<div>{_.user}</div>
				</div>
				{/* <Button text="Approve" disabled={!_.canApprove} /> */}
				{_.formData ? (
					<Button
						style={buttonClasses.lynkitBlackFill}
						text="View Form Data"
						onClick={() => {
							setApprovalFormId(_.id);
						}}
					/>
				) : null}
				{approvalFormId == _.id ? (
					<Dialog title="Approval Form Data" open={true} handleClose={() => setApprovalFormId("")} noBtn>
						{_.approvalTableHeader?.length
							? _.approvalTableHeader.map((header, i) => {
									let value = findDeepValue(_.formData, header.name?.split("."));
									if (Array.isArray(value)) {
										value = new Array(...value);
									} else {
										value = String(value);
									}
									const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
									if (new Date(value) != "Invalid Date" && isoPattern.test(value)) {
										value = formatDate(value);
									}
									return (
										<div className="detail_row" key={i}>
											<div className="detail_row_title">{header.label}</div>
											<div className="detail_row_value">
												{Array.isArray(value) ? (
													<span style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
														{value.map((el, i) =>
															isFilePath(el) ? <FilePreview key={i} fileKey={el} extension={String(el).split(".").pop()} /> : capitalizeCamelCase(el)
														)}
													</span>
												) : isFilePath(value) ? (
													<FilePreview fileKey={value} extension={value.split(".").pop()} />
												) : (
													capitalizeCamelCase(value)
												)}
											</div>
										</div>
									);
							  })
							: Object.keys(_.formData).map((key, i) => {
									let value = _.formData[key];
									if (Array.isArray(value)) {
										value = new Array(...value);
									} else {
										value = String(value);
									}
									const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
									if (new Date(value) != "Invalid Date" && isoPattern.test(value)) {
										value = formatDate(value);
									}
									return (
										<div className="detail_row" key={i}>
											<div className="detail_row_title">{capitalizeSentence(parseUnderscore(capitalizeCamelCase(key)))}</div>
											<div className="detail_row_value">
												{Array.isArray(value) ? (
													<span style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
														{value.map((el, i) =>
															isFilePath(el) ? <FilePreview key={i} fileKey={el} extension={String(el).split(".").pop()} /> : capitalizeCamelCase(el)
														)}
													</span>
												) : isFilePath(value) ? (
													<FilePreview fileKey={value} extension={value.split(".").pop()} />
												) : (
													capitalizeCamelCase(value)
												)}
											</div>
										</div>
									);
							  })}
					</Dialog>
				) : null}
			</div>
		);
	});

	let statusUi = <div className="approval_status_wrap">{isApprovalConfigLoading || isApprovalDetailLoading ? <Loader size="1rem" /> : approvalsUi}</div>;

	return statusUi;
}
function getApprovalStatus(approvalDetail) {
	return approvalDetail
		? approvalDetail.approvalStatus?.toLowerCase() == "hold"
			? "Hold"
			: approvalDetail.approvalStatus.includes("moveTo")
			? "Move To"
			: approvalDetail.approvalStatus?.toLowerCase() == "pending"
			? "Pending"
			: approvalDetail.isRejected
			? "Rejected"
			: "Approved"
		: "Pending";
}
